/**
 * Marked tokenizer extension for TeX math delimiters (\[...\] and \(...\)).
 * Emits `blockKatex`/`inlineKatex` tokens compatible with marked-katex-extension renderers.
 * Runs inside marked's tokenizer pipeline so code blocks/spans are already consumed,
 * eliminating false-positive matches inside code.
 */
import type { MarkedExtension, Tokens } from 'marked';

export function markedTexMath(): MarkedExtension {
    return {
        extensions: [
            // Block-level \[...\] — matches when the delimiter starts a new block.
            {
                name: 'texBlockMath',
                level: 'block' as const,
                start(src: string): number | undefined {
                    const idx = src.indexOf('\\[');
                    return idx !== -1 ? idx : undefined;
                },
                tokenizer(src: string): Tokens.Generic | undefined {
                    const match = src.match(/^\\\[([\s\S]*?)\\\]/);
                    if (match) {
                        return {
                            type: 'blockKatex',
                            raw: match[0],
                            text: match[1].trim(),
                            displayMode: true,
                        };
                    }
                    return undefined;
                },
            },
            // Inline \(...\) — standard TeX inline math.
            {
                name: 'texInlineMath',
                level: 'inline' as const,
                start(src: string): number | undefined {
                    const idx = src.indexOf('\\(');
                    return idx !== -1 ? idx : undefined;
                },
                tokenizer(src: string): Tokens.Generic | undefined {
                    const match = src.match(/^\\\(((?:[^\\]|\\.)*?)\\\)/);
                    if (match) {
                        return {
                            type: 'inlineKatex',
                            raw: match[0],
                            text: match[1].trim(),
                            displayMode: false,
                        };
                    }
                    return undefined;
                },
            },
            // Inline \[...\] — catches display math that appears mid-paragraph
            // (single-line only to avoid greedy cross-paragraph matching).
            {
                name: 'texInlineDisplayMath',
                level: 'inline' as const,
                start(src: string): number | undefined {
                    const idx = src.indexOf('\\[');
                    return idx !== -1 ? idx : undefined;
                },
                tokenizer(src: string): Tokens.Generic | undefined {
                    const match = src.match(/^\\\[([^\n]*?)\\\]/);
                    if (match) {
                        return {
                            type: 'inlineKatex',
                            raw: match[0],
                            text: match[1].trim(),
                            displayMode: true,
                        };
                    }
                    return undefined;
                },
            },
        ],
    };
}
